'use client'

import { MessageSquarePlus, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'

async function postUserComment({ appKey, comment }) {
  const { apiWithAuth } = await import('@/lib/auth-fetch')

  return apiWithAuth({
    method: 'POST',
    url: 'api/user-comments',
    data: { appKey, comment },
  })
}

export default function UserCommentButton({ appKey, floating = true }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')

  const maxLen = 300
  const remaining = useMemo(() => maxLen - (text?.length ?? 0), [text])

  const mutation = useMutation({
    mutationFn: ({ appKey, comment }) => postUserComment({ appKey, comment }),
    onSuccess: () => {
      setOpen(false)
      setText('')
    },
  })

  const canSend = !mutation.isPending && String(text).trim().length > 0 && String(text).trim().length <= maxLen

  const onClose = () => {
    if (mutation.isPending) return
    setOpen(false)
  }

  const onSubmit = () => {
    const comment = String(text).trim()
    if (!comment) return
    mutation.mutate({ appKey, comment })
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          position: floating ? 'absolute' : 'relative',
          top: floating ? 16 : 'auto',
          right: floating ? 16 : 'auto',
          zIndex: floating ? 10001 : 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          borderRadius: 999,
          border: '1px solid rgba(255,255,255,0.18)',
          background: 'rgba(0,0,0,0.35)',
          color: '#fff',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 600,
          backdropFilter: 'blur(6px)',
        }}
        aria-label={t("comment.leave_comment")}
        title={t("comment.leave_comment")}
      >
        <MessageSquarePlus size={16} />
        <span>{t("comment.leave_comment")}</span>
      </button>

      {open && typeof window !== 'undefined'
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              onClick={onClose}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.6)',
                zIndex: 100000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  maxWidth: 560,
                  background: '#111113',
                  borderRadius: 12,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.35)',
                  border: '1px solid rgba(255,255,255,0.10)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.10)',
                  }}
                >
                  <div style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{t("comment.leave_comment")}</div>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={mutation.isPending}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: mutation.isPending ? 'rgba(255,255,255,0.35)' : '#fff',
                      cursor: mutation.isPending ? 'not-allowed' : 'pointer',
                      padding: 4,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    aria-label={t("close")}
                  >
                    <X size={18} />
                  </button>
                </div>

                <div style={{ padding: 16 }}>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    maxLength={maxLen}
                    placeholder={t("comment.placeholder")}
                    disabled={mutation.isPending}
                    style={{
                      width: '100%',
                      minHeight: 120,
                      resize: 'vertical',
                      borderRadius: 10,
                      border: '1px solid rgba(255,255,255,0.12)',
                      background: 'rgba(0,0,0,0.25)',
                      color: '#fff',
                      padding: 12,
                      fontSize: 13,
                      outline: 'none',
                      lineHeight: 1.4,
                    }}
                  />

                  <div
                    style={{
                      marginTop: 8,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <div style={{ color: remaining < 0 ? '#ffb4b4' : 'rgba(255,255,255,0.65)', fontSize: 12 }}>
                      {t("comment.chars_remaining", { remaining })}
                    </div>

                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={mutation.isPending}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 10,
                          border: '1px solid rgba(255,255,255,0.18)',
                          background: 'transparent',
                          color: mutation.isPending ? 'rgba(255,255,255,0.35)' : '#fff',
                          cursor: mutation.isPending ? 'not-allowed' : 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {t("cancel")}
                      </button>
                      <button
                        type="button"
                        onClick={onSubmit}
                        disabled={!canSend}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 10,
                          border: '1px solid rgba(255,255,255,0.18)',
                          background: canSend ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
                          color: canSend ? '#fff' : 'rgba(255,255,255,0.35)',
                          cursor: canSend ? 'pointer' : 'not-allowed',
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {mutation.isPending ? t("comment.sending") : t("comment.send")}
                      </button>
                    </div>
                  </div>

                  {mutation.isError ? (
                    <div style={{ marginTop: 10, color: '#ffb4b4', fontSize: 12 }}>
                      {t("comment.error")}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  )
}
